import { Smartphone } from 'lucide-react';
import { CILT_LABELS, type CiltType } from '@odr/shared';

import { Badge, Button, Modal, QualityWarning, SafetyWarning } from '../../components/ui';
import { humanise } from '../../lib/format';

interface PreviewItem {
  id: string;
  seq: number;
  task: string;
  itemType: string;
  ciltType: string;
  toolMethod: string | null;
  standard: string | null;
  instruction: string | null;
  isRequired: boolean;
  minValue: number | null;
  maxValue: number | null;
  targetValue: number | null;
  unit: string | null;
  recordOnly: boolean;
  isSafetyCritical: boolean;
  safetyWarning: string | null;
  qualityWarning: string | null;
  options: Array<{ value: string; label: string; result: string }>;
}

/**
 * What the operator will see.
 *
 * Rendered at phone width because that is where the checklist is actually
 * performed — an author looking at a desktop table has no idea whether their
 * task text fits on the device.
 */
export function TemplatePreview({
  templateName,
  content,
  onClose,
}: {
  templateName: string;
  content: {
    version: { versionNo: number; status: string };
    sections: Array<{
      id: string;
      title: string;
      description: string | null;
      items: PreviewItem[];
    }>;
  };
  onClose: () => void;
}) {
  const total = content.sections.reduce((n, s) => n + s.items.length, 0);

  return (
    <Modal
      open
      onClose={onClose}
      title="Preview — as the operator sees it"
      footer={
        <Button fullWidth onClick={onClose}>
          Close preview
        </Button>
      }
    >
      <div className="space-y-3">
        <p className="flex items-center gap-2 text-xs text-steel-500">
          <Smartphone aria-hidden className="size-4" />
          Shown at phone width. {total} task{total === 1 ? '' : 's'} · v{content.version.versionNo}{' '}
          {humanise(content.version.status)}
        </p>

        <div className="mx-auto w-full max-w-[375px] overflow-hidden rounded-2xl border-4 border-steel-800 bg-steel-100">
          <div className="bg-white px-4 py-3">
            <h3 className="text-base font-bold text-steel-900">{templateName}</h3>
            <p className="text-sm text-steel-600">FILL-01 · Example Machine · Example Line</p>
            <div className="mt-3">
              <div className="mb-1 flex justify-between text-xs font-semibold text-steel-600">
                <span>Progress</span>
                <span className="tabular-nums">0 / {total}</span>
              </div>
              <div className="h-2.5 w-full rounded-full bg-steel-200">
                <div className="h-full w-0 rounded-full bg-info-500" />
              </div>
            </div>
          </div>

          {content.sections.map((section) => (
            <section key={section.id}>
              <h4 className="border-y border-steel-200 bg-steel-50 px-4 py-2 text-xs font-extrabold tracking-widest text-steel-600 uppercase">
                {section.title}
                {section.description ? (
                  <span className="ml-2 font-medium text-steel-400 normal-case">
                    {section.description}
                  </span>
                ) : null}
              </h4>

              {section.items.length === 0 ? (
                <p className="bg-white px-4 py-4 text-sm text-steel-400">
                  No tasks in this section — it will not appear on the device.
                </p>
              ) : (
                section.items.map((item) => (
                  <article
                    key={item.id}
                    className="border-b-8 border-steel-100 bg-white px-4 py-4 last:border-b-0"
                  >
                    <div className="flex items-start gap-3">
                      <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-steel-100 text-sm font-bold text-steel-600">
                        {item.seq}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap gap-1.5">
                          <span className="rounded bg-steel-200 px-1.5 py-0.5 text-[10px] font-bold tracking-wider text-steel-700 uppercase">
                            {CILT_LABELS[item.ciltType as CiltType] ?? item.ciltType}
                          </span>
                          {!item.isRequired ? <Badge>Optional</Badge> : null}
                        </div>
                        <h5 className="mt-1 text-base leading-snug font-bold text-steel-900">
                          <span className="text-steel-400">#{item.seq}</span> {item.task}
                        </h5>
                        {item.standard ? (
                          <p className="mt-1 text-sm text-steel-600">
                            <span className="font-semibold text-steel-500">Standard: </span>
                            {item.standard}
                          </p>
                        ) : null}
                        {item.toolMethod ? (
                          <p className="text-xs text-steel-500">Method: {item.toolMethod}</p>
                        ) : null}
                      </div>
                    </div>

                    {item.safetyWarning ? (
                      <div className="mt-3">
                        <SafetyWarning>{item.safetyWarning}</SafetyWarning>
                      </div>
                    ) : null}
                    {item.qualityWarning ? (
                      <div className="mt-2">
                        <QualityWarning>{item.qualityWarning}</QualityWarning>
                      </div>
                    ) : null}

                    {item.instruction ? (
                      <p className="mt-3 rounded-lg bg-steel-50 p-3 text-sm whitespace-pre-line text-steel-700">
                        {item.instruction}
                      </p>
                    ) : null}

                    <div className="mt-4">
                      <ControlPreview item={item} />
                    </div>
                  </article>
                ))
              )}
            </section>
          ))}
        </div>
      </div>
    </Modal>
  );
}

function ControlPreview({ item }: { item: PreviewItem }) {
  switch (item.itemType) {
    case 'PASS_FAIL':
    case 'YES_NO':
    case 'CONFIRMATION': {
      const [yes, no] =
        item.itemType === 'PASS_FAIL'
          ? ['PASS', 'FAIL']
          : item.itemType === 'YES_NO'
            ? ['YES', 'NO']
            : ['DONE', 'NOT DONE'];
      return (
        <div className="space-y-2">
          <div className="flex gap-2">
            <span className="answer-button border-steel-300 bg-white text-steel-700">{yes}</span>
            <span className="answer-button border-steel-300 bg-white text-steel-700">{no}</span>
          </div>
          {!item.isSafetyCritical && item.itemType !== 'CONFIRMATION' ? (
            <span className="flex min-h-11 w-full items-center justify-center rounded-lg border-2 border-steel-200 bg-white text-sm font-semibold text-steel-500">
              Not applicable
            </span>
          ) : null}
          {item.isSafetyCritical ? (
            <p className="text-xs font-semibold text-alarm-700">
              Safety critical — “not applicable” is not offered.
            </p>
          ) : null}
        </div>
      );
    }

    case 'NUMERIC':
    case 'COUNTER':
      return (
        <div>
          <div className="flex items-center gap-2">
            <span className="grid min-h-touch w-full place-items-center rounded-lg border-2 border-steel-300 text-3xl font-bold text-steel-300">
              0
            </span>
            {item.unit ? (
              <span className="shrink-0 text-lg font-bold text-steel-500">{item.unit}</span>
            ) : null}
          </div>
          <p className="mt-1.5 text-center text-xs text-steel-500">
            {item.recordOnly ? (
              <>
                Target {item.targetValue ?? '—'}
                {item.unit ? ` ${item.unit}` : ''} — the reading is recorded, not scored.
              </>
            ) : item.minValue != null && item.maxValue != null ? (
              <>
                Acceptable range: {item.minValue} to {item.maxValue}
                {item.unit ? ` ${item.unit}` : ''}
              </>
            ) : (
              'Record the reading.'
            )}
          </p>
          {item.recordOnly ? (
            <p className="mt-2 rounded-lg bg-info-50 p-2 text-center text-xs font-semibold text-info-700">
              ℹ Recorded — Not Scored
            </p>
          ) : null}
        </div>
      );

    case 'DROPDOWN':
    case 'MULTI_SELECT':
      return (
        <div className="grid gap-2">
          {item.options.map((option) => (
            <span
              key={option.value}
              className="answer-button justify-start border-steel-300 bg-white text-steel-800"
            >
              {option.label}
              {option.result === 'ABNORMAL' ? (
                <Badge tone="alarm" className="ml-auto">
                  Abnormal
                </Badge>
              ) : null}
            </span>
          ))}
        </div>
      );

    case 'TEXT':
    case 'SIGNATURE':
      return (
        <span className="block min-h-20 w-full rounded-lg border border-steel-300 bg-white p-3 text-sm text-steel-400">
          {item.itemType === 'SIGNATURE' ? 'Type your full name' : 'Type your answer'}
        </span>
      );

    default:
      return (
        <span className="block rounded-lg bg-steel-50 p-3 text-sm text-steel-500">
          {humanise(item.itemType)} input
        </span>
      );
  }
}
